import { getSupabase } from './client';
import { toDataError } from './errors';
import type { WorkspaceScope } from './workspace';

/**
 * Stage 5 (Approved) performance score — see supabase/migrations/20260929000000_task_scores.sql.
 *
 * The score deliberately does NOT live on the task document: records.data is readable
 * by any employee with 'tasks' read permission, and RLS cannot mask one key inside a
 * jsonb blob. public.task_scores is gated to admin/manager, so for an employee every
 * read below simply returns no rows.
 */
export interface TaskScore {
  readonly taskId: string;
  readonly score: number;
  readonly comment: string;
  readonly byName: string;
  readonly at: string;
}

export type TaskScoreMap = Record<string, TaskScore>;

export async function fetchTaskScores(scope: WorkspaceScope): Promise<TaskScoreMap> {
  const { data, error } = await getSupabase()
    .from('task_scores')
    .select('task_id, score, comment, scored_by_name, scored_at')
    .eq('workspace_id', scope.workspaceId)
    .eq('app_key', scope.appKey);
  if (error) throw toDataError(error);
  const out: TaskScoreMap = {};
  for (const row of data ?? []) {
    out[row.task_id] = {
      taskId: row.task_id,
      score: row.score,
      comment: row.comment ?? '',
      byName: row.scored_by_name ?? '',
      at: row.scored_at,
    };
  }
  return out;
}

export async function upsertTaskScore(
  scope: WorkspaceScope,
  userId: string,
  entry: { taskId: string; score: number; comment: string; byName: string },
): Promise<TaskScore> {
  const score = Math.max(0, Math.min(10, Math.round(entry.score)));
  const { data, error } = await getSupabase()
    .from('task_scores')
    .upsert(
      {
        workspace_id: scope.workspaceId,
        app_key: scope.appKey,
        task_id: entry.taskId,
        score,
        comment: entry.comment.slice(0, 4000),
        scored_by: userId,
        scored_by_name: entry.byName.slice(0, 200),
        scored_at: new Date().toISOString(),
      },
      { onConflict: 'workspace_id,app_key,task_id' },
    )
    .select('task_id, score, comment, scored_by_name, scored_at')
    .single();
  if (error) throw toDataError(error);
  return {
    taskId: data.task_id,
    score: data.score,
    comment: data.comment ?? '',
    byName: data.scored_by_name ?? '',
    at: data.scored_at,
  };
}

export async function deleteTaskScore(scope: WorkspaceScope, taskId: string): Promise<void> {
  const { error } = await getSupabase()
    .from('task_scores')
    .delete()
    .eq('workspace_id', scope.workspaceId)
    .eq('app_key', scope.appKey)
    .eq('task_id', taskId);
  if (error) throw toDataError(error);
}
